import {
  MAX_CAMADAS_POR_CELULA,
  MAX_CAMADAS_POR_FOLHA,
  MAX_FIGURINHAS_DISTINTAS_POR_FOLHA,
  type Camada,
} from "@foto/shared";

/**
 * Os tetos de camadas, para a tela avisar **antes** de o servidor recusar o pedido.
 *
 * Há três: 12 camadas por foto (`MAX_CAMADAS_POR_CELULA`), 60 camadas na folha inteira (`MAX_CAMADAS_POR_FOLHA`) e
 * 24 figurinhas **diferentes** na folha (`MAX_FIGURINHAS_DISTINTAS_POR_FOLHA`). O servidor valida os três
 * no pedido da folha e no de imprimir: cada célula pode estar nas suas 12 e a folha, ainda assim, passar do teto.
 * Os números vêm de `@foto/shared`, os mesmos do servidor — nada é copiado aqui.
 */

export interface TotaisDaFolha {
  camadas: number;
  /** Figurinhas diferentes (a mesma figurinha em duas fotos conta uma vez). */
  figurinhas: number;
}

export function totaisDasCamadas(camadas: readonly Camada[]): TotaisDaFolha {
  const ids = new Set<number>();
  for (const c of camadas) if (c.tipo === "imagem") ids.add(c.figurinhaId);
  return { camadas: camadas.length, figurinhas: ids.size };
}

/** Os totais de uma folha: as camadas de cada célula (`null` é célula vazia). */
export function totaisDaFolha(celulas: readonly (readonly Camada[] | null)[]): TotaisDaFolha {
  return totaisDasCamadas(celulas.flatMap((c) => c ?? []));
}

const plural = (n: number, um: string, varios: string): string => (n === 1 ? um : varios);

export const MENSAGEM_FOTO_CHEIA = `Esta foto já tem ${MAX_CAMADAS_POR_CELULA} camadas, o máximo. Apague uma para acrescentar outra.`;
export const MENSAGEM_FOLHA_CHEIA = `A folha chegou ao limite de ${MAX_CAMADAS_POR_FOLHA} camadas; remova alguma (desta foto ou de outra) para acrescentar.`;
export const MENSAGEM_FIGURINHAS_DEMAIS = `A folha já usa ${MAX_FIGURINHAS_DISTINTAS_POR_FOLHA} figurinhas diferentes, o máximo; use uma que já esteja nela, ou remova alguma.`;

/** O que se quer acrescentar: um texto, ou a imagem desta figurinha. `null`: só perguntar se cabe mais uma. */
export type CamadaPretendida = { tipo: "texto" } | { tipo: "imagem"; figurinhaId: number } | null;

/**
 * Por que mais uma camada **não cabe**, em português, ou `null` se cabe. `daCelula` são as camadas da foto que se
 * edita; `outras`, as das demais fotos da folha. A figurinha que a folha já usa não conta como nova: repeti-la é
 * livre; só a figurinha diferente pesa no teto das 24.
 */
export function motivoDeNaoCaber(args: {
  daCelula: readonly Camada[];
  outras: readonly Camada[];
  nova?: CamadaPretendida;
}): string | null {
  const { daCelula, outras, nova = null } = args;
  if (daCelula.length >= MAX_CAMADAS_POR_CELULA) return MENSAGEM_FOTO_CHEIA;

  const totais = totaisDasCamadas([...outras, ...daCelula]);
  if (totais.camadas >= MAX_CAMADAS_POR_FOLHA) return MENSAGEM_FOLHA_CHEIA;

  if (nova?.tipo === "imagem" && totais.figurinhas >= MAX_FIGURINHAS_DISTINTAS_POR_FOLHA) {
    const jaUsada = [...outras, ...daCelula].some(
      (c) => c.tipo === "imagem" && c.figurinhaId === nova.figurinhaId,
    );
    if (!jaUsada) return MENSAGEM_FIGURINHAS_DEMAIS;
  }
  return null;
}

/**
 * Por que a folha **ficaria acima do teto** depois de "Aplicar em todas as fotos", ou `null`. Quem chama monta as
 * camadas de cada célula como ficariam e pergunta antes de aplicar: o servidor recusaria o pedido inteiro.
 */
export function motivoDaAplicacaoNaoCaber(
  celulasDepois: readonly (readonly Camada[] | null)[],
): string | null {
  const totais = totaisDaFolha(celulasDepois);
  if (totais.camadas > MAX_CAMADAS_POR_FOLHA) {
    return `Aplicar deixaria a folha com ${totais.camadas} camadas, e o máximo é ${MAX_CAMADAS_POR_FOLHA}. Tire camadas desta foto (ou das outras) e tente de novo; nada foi aplicado.`;
  }
  if (totais.figurinhas > MAX_FIGURINHAS_DISTINTAS_POR_FOLHA) {
    return `Aplicar deixaria a folha com ${totais.figurinhas} ${plural(totais.figurinhas, "figurinha", "figurinhas")} diferentes, e o máximo é ${MAX_FIGURINHAS_DISTINTAS_POR_FOLHA}. Troque algumas por figurinhas que a folha já usa; nada foi aplicado.`;
  }
  return null;
}
